import React, {useState, useEffect} from 'react';
import {Box, Button, Stepper, Step, StepLabel, Paper, AppBar, Toolbar} from '@mui/material';
import {useNavigate} from 'react-router-dom';
import LanguageThemeBar from '../LanguageThemeBar';
import {useTranslation} from "react-i18next";
import ToolDetailsStep from './ToolDetailStep';
import ToolConfigStep from './ToolConfigStep';
import axios from 'axios';
import ToolReviewStep from './ToolReviewStep';
import {buildExtractionTableDataWithGroups} from "../PdfViewer/ViewerContainer/ViewerContainer";
import {API_BASE} from '../../apiBase';
import {apiFetch} from '../../api/api';


export default function CreateToolPage() {
    const [activeStep, setActiveStep] = useState(0);
    const navigate = useNavigate();
    const {t} = useTranslation();
    const [toolDetails, setToolDetails] = useState({
        name: '',
        description: '',
        provider: '',
        model: '',
    });
    const [errors, setErrors] = useState({})

    const steps = [
        t("steps.tool_details"),
        t("steps.configure_settings"),
        t("steps.review_save")
    ];

    const [selectedAnnotations, setSelectedAnnotations] = useState([]);
    const [allAnnotations, setAllAnnotations] = useState([]);
    const [groupMeta, setGroupMeta] = useState({});
    const [exampleFile, setExampleFile] = useState(null);
    const [useOcr, setUseOcr] = useState(false);
    const [useDeepScan, setUseDeepScan] = useState(false);
    const [allowReExtraction, setAllowReExtraction] = useState(false);
    const [spatialCloseness, setSpatialCloseness] = useState(true);
    const [useSpatialLocality, setUseSpatialLocality] = useState(false); // Default OFF - opt-in to avoid false negatives

    // Debug: log when annotations change
    useEffect(() => {
        console.log('[CreateToolPage] allAnnotations changed:', allAnnotations?.length);
        // Reset the re-extraction flag once annotations are updated
        if (allAnnotations?.length > 0 && allowReExtraction) {
            setAllowReExtraction(false);
        }
    }, [allAnnotations, allowReExtraction]);

    const handleNext = () => {
        if (activeStep === 0) {
            const newErrors = {};
            if (!toolDetails.name.trim()) {
                newErrors.name = t("tool_details.name_required");
            }

            if (Object.keys(newErrors).length > 0) {
                setErrors(newErrors);
                return; // Stop from advancing
            }
        }

        setErrors({});
        setActiveStep((prevStep) => prevStep + 1);
    };

    const handleBack = () => {
        setActiveStep((prevStep) => prevStep - 1);
    };

    const handleFinish = async () => {
        try {
            // Debug: log toolDetails before saving
            console.log('[CreateToolPage] Saving tool with details:', toolDetails);

            // Deep-clone then drop any table-type annotations before persisting
            const cloned = JSON.parse(JSON.stringify(selectedAnnotations));
            const finalAnnotations = cloned.filter(a => a?.type !== 'table');
            const token = localStorage.getItem('token');

            // Build headers from the filtered set (no tables)
            const {headers} = buildExtractionTableDataWithGroups(finalAnnotations, groupMeta);
            if (exampleFile) {
                // Send multipart to also store the example as a ToolExecution
                const form = new FormData();
                form.append('pdf', exampleFile);
                form.append('toolDetails', JSON.stringify(toolDetails));
                form.append('annotations', JSON.stringify(finalAnnotations));
                form.append('headerConfig', JSON.stringify(headers));
                form.append('groupMeta', JSON.stringify(groupMeta));
                form.append('use_ocr', useOcr ? 'true' : 'false');
                form.append('use_deep_scan', useDeepScan ? 'true' : 'false');
                form.append('spatialCloseness', spatialCloseness ? 'true' : 'false');
                form.append('useSpatialLocality', useSpatialLocality ? 'true' : 'false');

                const res = await apiFetch(`${API_BASE}/save_tool_with_example`, {
                    method: 'POST',
                    body: form, // no manual Content-Type here
                });
                if (!res.ok) {
                    const msg = (await res.text()) || 'Upload failed';
                    throw new Error(msg);
                }
            } else {
                // Fallback to old JSON-only behavior
                const res = await apiFetch(`${API_BASE}/save_tool`, {
                    method: 'POST',
                    headers: {'Content-Type': 'application/json'},
                    body: JSON.stringify({
                        toolDetails,
                        annotations: finalAnnotations,
                        headerConfig: headers,
                        groupMeta,
                        spatialCloseness,
                        useSpatialLocality,
                    }),
                });
                if (!res.ok) {
                    const msg = (await res.text()) || 'Save failed';
                    throw new Error(msg);
                }
            }
            navigate('/home');
        } catch (err) {
            console.error("Error saving tool", err);
            if (err.response) {
                console.error("Response data:", err.response.data);
                console.error("Status:", err.response.status);
                console.error("Headers:", err.response.headers);
            } else {
                console.error("Request data:", err.request);
            }
        }
    };

    return (
        <Box sx={{display: 'flex', flexDirection: 'column', height: '100vh', overflow: 'hidden'}}>
            {/* Header */}
            <AppBar position="static" color="transparent" elevation={0} sx={{flexShrink: 0}}>
                <Toolbar
                    sx={{
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        minHeight: 48, // 👈 reduce toolbar height (default is 64 or 56 on mobile)
                        px: 1,
                    }}
                >
                    <Button
                        variant="outlined"
                        onClick={() => navigate('/home')}
                        sx={{ml: 1, whiteSpace: 'nowrap'}}
                    >
                        {t("homepage")}
                    </Button>

                    <Box
                        sx={{
                            flexGrow: 1,
                            mx: 2,
                            mt: 1.5, // 👈 reduce top margin
                        }}
                    >
                        <Stepper
                            activeStep={activeStep}
                            alternativeLabel
                            sx={{
                                '& .MuiStepLabel-label': {
                                    fontSize: '0.85rem',
                                },
                                '& .MuiStepIcon-root': {
                                    fontSize: '1.4rem',
                                },
                                p: 0, // remove default padding
                                minHeight: 0,
                            }}
                        >
                            {steps.map((label) => (
                                <Step key={label}>
                                    <StepLabel>{label}</StepLabel>
                                </Step>
                            ))}
                        </Stepper>
                    </Box>

                    <Box display="flex" alignItems="center">
                        <LanguageThemeBar/>
                    </Box>
                </Toolbar>

            </AppBar>


            {/* Content below header */}
            <Box
                sx={{
                    flexGrow: 1,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'stretch',
                    p: 2,
                    minHeight: 0,
                    overflow: 'hidden',
                }}
            >
                <Paper
                    elevation={3}
                    sx={{
                        flexGrow: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        pt: 1, // top padding (16px)
                        pr: 3, // right padding (24px)
                        pb: 1, // bottom padding (16px)
                        pl: 3, // left padding (24px)
                        width: '100%',
                        minHeight: 0,
                        overflow: 'hidden',
                    }}
                >
                    {/* Content area that fills vertical space */}
                    <Box sx={{flexGrow: 1, overflow: 'hidden', minHeight: 0}}>
                        {activeStep === 0 && (
                            <ToolDetailsStep
                                toolDetails={toolDetails}
                                setToolDetails={setToolDetails}
                                errors={errors}
                            />
                        )}
                        {activeStep === 1 && (
                            <ToolConfigStep
                                selectedAnnotations={selectedAnnotations}
                                setSelectedAnnotations={setSelectedAnnotations}
                                allAnnotations={allAnnotations}
                                setAllAnnotations={setAllAnnotations}
                                groupMeta={groupMeta}
                                setGroupMeta={setGroupMeta}
                                onExampleFileSelected={setExampleFile}
                                exampleFile={exampleFile}
                                useOcr={useOcr}
                                setUseOcr={setUseOcr}
                                useDeepScan={useDeepScan}
                                setUseDeepScan={setUseDeepScan}
                                allowReExtraction={allowReExtraction}
                                setAllowReExtraction={setAllowReExtraction}
                                spatialCloseness={spatialCloseness}
                                setSpatialCloseness={setSpatialCloseness}
                                useSpatialLocality={useSpatialLocality}
                                setUseSpatialLocality={setUseSpatialLocality}
                                toolDetails={toolDetails}
                                setToolDetails={setToolDetails}
                            />
                        )}
                        {activeStep === 2 && (
                            <ToolReviewStep
                                selectedAnnotations={selectedAnnotations}
                                groupMeta={groupMeta}
                            />
                        )}
                    </Box>

                    {/* Navigation buttons */}
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            gap: 2,
                            pt: 0,
                            pb: 0
                        }}
                    >
                        <Button
                            color="inherit"
                            disabled={activeStep === 0}
                            onClick={handleBack}
                            sx={{mr: 1}}
                        >
                            {t("back")}
                        </Button>
                        {activeStep === steps.length - 1 ? (
                            <Button variant="contained" onClick={handleFinish}>
                                {t("finish")}
                            </Button>
                        ) : (
                            <Button
                                variant="contained"
                                onClick={handleNext}
                                disabled={activeStep === 1 && !exampleFile}
                            >
                                {t("next")}
                            </Button>
                        )}
                    </Box>
                </Paper>
            </Box>
        </Box>
    );
}
